import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Container,
  Stack,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  NetworkCheck as NetworkIcon,
  Assessment as AssessmentIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  PersonAdd as PersonAddIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { organizationsApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import LoadingSpinner from '../LoadingSpinner';

const OrganizationListPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [filteredOrganizations, setFilteredOrganizations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCountry, setFilterCountry] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Create Organization Dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [newOrgData, setNewOrgData] = useState({
    name: '',
    country: '',
    timezone: '',
    contact_email: '',
    contact_phone: ''
  });

  // Create Manager Dialog
  const [createManagerDialogOpen, setCreateManagerDialogOpen] = useState(false);
  const [selectedOrgForManager, setSelectedOrgForManager] = useState(null);
  const [createManagerLoading, setCreateManagerLoading] = useState(false);
  const [newManagerData, setNewManagerData] = useState({
    username: '',
    email: '',
    full_name: '',
    temporaryPassword: '',
    sendWelcomeEmail: true
  });

  // Edit Organization Dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editOrgData, setEditOrgData] = useState({
    id: null,
    name: '',
    country: '',
    timezone: '',
    contact_email: '',
    contact_phone: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    filterData();
  }, [data, searchTerm, filterCountry]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await organizationsApi.getAdminList();
      setData(response.data);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      setError('Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      const response = await organizationsApi.getAdminList();
      setData(response.data);
    } catch (error) {
      console.error('Error refreshing organizations:', error);
      setError('Failed to refresh organizations');
    } finally {
      setRefreshing(false);
    }
  };

  const filterData = () => {
    if (!data?.organizations) return;

    let filtered = data.organizations;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(org =>
        org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        org.country?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        org.contact_email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by country
    if (filterCountry) {
      filtered = filtered.filter(org => org.country === filterCountry);
    }

    setFilteredOrganizations(filtered);
    setPage(0); // Reset to first page when filtering
  };

  const handleCreateOrganization = async () => {
    try {
      setCreateLoading(true);
      await organizationsApi.create(newOrgData);
      setCreateDialogOpen(false);
      setNewOrgData({
        name: '',
        country: '',
        timezone: '',
        contact_email: '',
        contact_phone: ''
      });
      await fetchData(); // Refresh data
    } catch (error) {
      console.error('Error creating organization:', error);
      setError('Failed to create organization');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleCreateManager = async () => {
    try {
      setCreateManagerLoading(true);
      await organizationsApi.createManager({
        ...newManagerData,
        organizationId: selectedOrgForManager.id
      });
      setCreateManagerDialogOpen(false);
      setSelectedOrgForManager(null);
      setNewManagerData({
        username: '',
        email: '',
        full_name: '',
        temporaryPassword: '',
        sendWelcomeEmail: true
      });
      await fetchData(); // Refresh data
    } catch (error) {
      console.error('Error creating manager:', error);
      setError('Failed to create manager');
    } finally {
      setCreateManagerLoading(false);
    }
  };

  const handleEditOrganization = (org) => {
    setEditOrgData({
      id: org.id,
      name: org.name,
      country: org.country || '',
      timezone: org.timezone || '',
      contact_email: org.contact_email || '',
      contact_phone: org.contact_phone || ''
    });
    setEditDialogOpen(true);
  };

  const handleUpdateOrganization = async () => {
    try {
      setEditLoading(true);
      await organizationsApi.update(editOrgData.id, {
        name: editOrgData.name,
        country: editOrgData.country,
        timezone: editOrgData.timezone,
        contact_email: editOrgData.contact_email,
        contact_phone: editOrgData.contact_phone
      });
      setEditDialogOpen(false);
      setEditOrgData({
        id: null,
        name: '',
        country: '',
        timezone: '',
        contact_email: '',
        contact_phone: ''
      });
      await fetchData(); // Refresh data
    } catch (error) {
      console.error('Error updating organization:', error);
      setError('Failed to update organization');
    } finally {
      setEditLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getCountries = () => {
    if (!data?.organizations) return [];
    const countries = [...new Set(data.organizations.map(org => org.country).filter(Boolean))];
    return countries.sort();
  };

  const handleViewOrganization = (organizationId) => {
    navigate(`/organizations/${organizationId}`);
  };

  // Early returns for loading and authentication
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading organizations...
        </Typography>
      </Container>
    );
  }

  // Handle authentication errors
  if (!user || user.role !== 'admin' || user.organizationId !== null) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Alert severity="error">
          Access denied. System Admin privileges required.
        </Alert>
      </Container>
    );
  }

  const paginatedOrganizations = filteredOrganizations.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Page Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <BusinessIcon sx={{ fontSize: 28, color: 'primary.main' }} />
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Organization Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            System admin organization oversight and management
          </Typography>
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={handleRefresh}
            disabled={refreshing}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Organization
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Statistics Overview */}
      {data?.summary && (
        <Paper sx={{ p: 3, borderRadius: 1, mb: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
            <AssessmentIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              Platform Overview
            </Typography>
          </Stack>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <BusinessIcon color="primary" sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {data.summary.total_organizations}
                      </Typography>
                      <Typography color="text.secondary">
                        Total Organizations
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <PeopleIcon color="success" sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {data.summary.total_users}
                      </Typography>
                      <Typography color="text.secondary">
                        Total Users
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <NetworkIcon color="info" sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {data.summary.total_networks}
                      </Typography>
                      <Typography color="text.secondary">
                        Total Networks
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <AssessmentIcon color="warning" sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {data.summary.active_organizations}
                      </Typography>
                      <Typography color="text.secondary">
                        Active (30 days)
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Search and Filters */}
      <Paper sx={{ p: 3, borderRadius: 1, mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
          <FilterIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Search & Filter Organizations
          </Typography>
        </Stack>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search organizations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Filter by Country</InputLabel>
              <Select
                value={filterCountry}
                onChange={(e) => setFilterCountry(e.target.value)}
                label="Filter by Country"
              >
                <MenuItem value="">All Countries</MenuItem>
                {getCountries().map(country => (
                  <MenuItem key={country} value={country}>{country}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={12} md={5}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                Showing {filteredOrganizations.length} of {data?.organizations?.length || 0} organizations
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Organizations Table */}
      <Paper sx={{ p: 3, borderRadius: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
          <BusinessIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Organizations ({filteredOrganizations.length})
          </Typography>
        </Stack>
        
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 'bold' } }}>
                <TableCell>Organization</TableCell>
                <TableCell>Country</TableCell>
                <TableCell align="center">Users</TableCell>
                <TableCell align="center">Networks</TableCell>
                <TableCell align="center">Assets</TableCell>
                <TableCell>Last Activity</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedOrganizations.map((org) => (
                <TableRow key={org.id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {org.name}
                      </Typography>
                      {org.contact_email && (
                        <Typography variant="caption" color="text.secondary">
                          {org.contact_email}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {org.country || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" fontWeight="bold">
                        {org.total_users}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {org.manager_count > 0 && (
                          <Chip size="small" label={`${org.manager_count}M`} color="warning" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                        )}
                        {org.inspector_count > 0 && (
                          <Chip size="small" label={`${org.inspector_count}I`} color="info" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                        )}
                        {org.viewer_count > 0 && (
                          <Chip size="small" label={`${org.viewer_count}V`} color="default" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" fontWeight="bold">
                      {org.network_count}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {org.section_count} sections
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" fontWeight="bold">
                      {org.asset_count}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {org.last_activity ? formatDate(org.last_activity) : 'No activity'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {formatDate(org.created_at)}
                    </Typography>
                    {org.created_by_name && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        by {org.created_by_name}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title="View Details">
                        <IconButton 
                          size="small" 
                          onClick={() => handleViewOrganization(org.id)}
                          sx={{ color: 'primary.main' }}
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Organization">
                        <IconButton 
                          size="small"
                          onClick={() => handleEditOrganization(org)}
                          sx={{ color: 'warning.main' }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Create Manager">
                        <IconButton 
                          size="small" 
                          onClick={() => {
                            setSelectedOrgForManager(org);
                            setCreateManagerDialogOpen(true);
                          }}
                          sx={{ color: 'success.main' }}
                        >
                          <PersonAddIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredOrganizations.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          sx={{ borderTop: 1, borderColor: 'divider' }}
        />
      </Paper>

      {/* Create Organization Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Organization</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Organization Name"
              value={newOrgData.name}
              onChange={(e) => setNewOrgData({ ...newOrgData, name: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Country"
              value={newOrgData.country}
              onChange={(e) => setNewOrgData({ ...newOrgData, country: e.target.value })}
              fullWidth
            />
            <TextField
              label="Timezone"
              value={newOrgData.timezone}
              onChange={(e) => setNewOrgData({ ...newOrgData, timezone: e.target.value })}
              fullWidth
              placeholder="e.g., Asia/Kuala_Lumpur"
            />
            <TextField
              label="Contact Email"
              type="email"
              value={newOrgData.contact_email}
              onChange={(e) => setNewOrgData({ ...newOrgData, contact_email: e.target.value })}
              fullWidth
            />
            <TextField
              label="Contact Phone"
              value={newOrgData.contact_phone}
              onChange={(e) => setNewOrgData({ ...newOrgData, contact_phone: e.target.value })}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateOrganization} 
            variant="contained"
            disabled={!newOrgData.name || createLoading}
          >
            {createLoading ? <CircularProgress size={20} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Manager Dialog */}
      <Dialog open={createManagerDialogOpen} onClose={() => setCreateManagerDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Create Manager for {selectedOrgForManager?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Username"
              value={newManagerData.username}
              onChange={(e) => setNewManagerData({ ...newManagerData, username: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Email"
              type="email"
              value={newManagerData.email}
              onChange={(e) => setNewManagerData({ ...newManagerData, email: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Full Name"
              value={newManagerData.full_name}
              onChange={(e) => setNewManagerData({ ...newManagerData, full_name: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Temporary Password (Optional)"
              value={newManagerData.temporaryPassword}
              onChange={(e) => setNewManagerData({ ...newManagerData, temporaryPassword: e.target.value })}
              fullWidth
              placeholder="Leave blank for auto-generated password"
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <input
                type="checkbox"
                checked={newManagerData.sendWelcomeEmail}
                onChange={(e) => setNewManagerData({ ...newManagerData, sendWelcomeEmail: e.target.checked })}
              />
              <Typography variant="body2">Send welcome email with login details</Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateManagerDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateManager} 
            variant="contained"
            disabled={!newManagerData.username || !newManagerData.email || !newManagerData.full_name || createManagerLoading}
          >
            {createManagerLoading ? <CircularProgress size={20} /> : 'Create Manager'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Organization Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Organization</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Organization Name"
              value={editOrgData.name}
              onChange={(e) => setEditOrgData({ ...editOrgData, name: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Country"
              value={editOrgData.country}
              onChange={(e) => setEditOrgData({ ...editOrgData, country: e.target.value })}
              fullWidth
            />
            <TextField
              label="Timezone"
              value={editOrgData.timezone}
              onChange={(e) => setEditOrgData({ ...editOrgData, timezone: e.target.value })}
              fullWidth
              placeholder="e.g., Asia/Kuala_Lumpur"
            />
            <TextField
              label="Contact Email"
              type="email"
              value={editOrgData.contact_email}
              onChange={(e) => setEditOrgData({ ...editOrgData, contact_email: e.target.value })}
              fullWidth
            />
            <TextField
              label="Contact Phone"
              value={editOrgData.contact_phone}
              onChange={(e) => setEditOrgData({ ...editOrgData, contact_phone: e.target.value })}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleUpdateOrganization} 
            variant="contained"
            disabled={!editOrgData.name || editLoading}
          >
            {editLoading ? <CircularProgress size={20} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default OrganizationListPage; 